import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { insertDocumentChecklistItemSchema, type DocumentChecklistItem } from "@shared/schema";
import { z } from "zod";

type ItemFormData = z.infer<typeof insertDocumentChecklistItemSchema>;

interface DocumentChecklistItemFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checklistId: string;
  item?: DocumentChecklistItem;
  nextSortOrder?: number;
  onItemCreated?: (item: DocumentChecklistItem) => void;
}

export function DocumentChecklistItemFormModal({
  open,
  onOpenChange,
  checklistId,
  item,
  nextSortOrder = 0,
  onItemCreated,
}: DocumentChecklistItemFormModalProps) {
  const { toast } = useToast();
  const isEdit = !!item;

  const defaultValues: Partial<ItemFormData> = {
    checklistId,
    documentName: "",
    description: "",
    isRequired: 1,
    sortOrder: nextSortOrder,
  };

  const form = useForm<ItemFormData>({
    resolver: zodResolver(insertDocumentChecklistItemSchema),
    defaultValues,
    mode: "onChange",
    criteriaMode: "all",
  });

  useEffect(() => {
    if (open) {
      if (item) {
        form.reset({
          checklistId: item.checklistId,
          documentName: item.documentName,
          description: item.description || "",
          isRequired: item.isRequired ?? 1,
          sortOrder: item.sortOrder ?? 0,
        });
      } else {
        form.reset({
          ...defaultValues,
          sortOrder: nextSortOrder,
        });
      }
    }
  }, [open, item, nextSortOrder, form]);

  const createMutation = useMutation({
    mutationFn: async (data: ItemFormData) => {
      const response = await apiRequest(
        "POST",
        `/api/crm/document-checklists/${checklistId}/items`,
        data
      );
      return response.json();
    },
    onSuccess: async (newItem: DocumentChecklistItem) => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/document-checklists", checklistId, "items"] });
      toast({
        title: "Document added",
        description: "The document has been added to the checklist.",
      });
      onOpenChange(false);
      if (onItemCreated) onItemCreated(newItem);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ItemFormData) =>
      apiRequest("PATCH", `/api/crm/document-checklist-items/${item?.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/document-checklists", checklistId, "items"] });
      toast({
        title: "Document updated",
        description: "The document has been updated successfully.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ItemFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Document" : "Add Document"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update document information"
              : "Add a new document to this checklist"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="documentName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Document Name *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Certificate of Incorporation"
                      data-testid="input-document-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ""}
                      placeholder="Additional notes about this document requirement..."
                      data-testid="input-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sort Order</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      value={field.value ?? 0}
                      onChange={(e) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))}
                      data-testid="input-sort-order"
                    />
                  </FormControl>
                  <FormDescription>
                    Order in which this document appears in the checklist
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isRequired"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value === 1}
                      onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                      data-testid="checkbox-is-required"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Required document</FormLabel>
                    <FormDescription>
                      This document is required for loan approval
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending} data-testid="button-save">
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? "Update" : "Add"} Document
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
